<script setup lang="ts">
  import { computed, type PropType } from 'vue'

  import CPAvatar from '@packages/ui/components/CPAvatar.vue'
  import CPText from '@packages/ui/components/CPText.vue'

  import { CalendarIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { DEFAULT_IMAGES, GOKOLLAB_ASSETS } from '@common/core-layer'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import ChannelIcon from '@communities/channels-layer/components/ChannelIcon.vue'
  import { formatEventTimes } from '@communities/events-layer/helpers'
  import type { IChannel } from '@communities/channels-layer/types/channels'

  import type { MentionItem } from '../types/mention.types'

  const props = defineProps({
    item: {
      type: Object as PropType<MentionItem>,
      required: true,
    },
    onClick: {
      type: Function as PropType<(item: MentionItem) => void>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  })

  // Format event date and times for display (same as EventsPicker)
  const formatEventDateTime = (item: MentionItem) => {
    if (item.type !== 'event') return null

    const displayStartTime = item.isRecurring
      ? item.nextOccurrence || item.startTime
      : item.startTime || (item.timestamp ? new Date(item.timestamp).toISOString() : undefined)

    if (!displayStartTime) return null

    const { date, fromTime, toTime } = formatEventTimes(displayStartTime, item.endTime || '')
    return {
      date,
      timeRange: `${fromTime} - ${toTime}`,
    }
  }

  const eventDateTime = computed(() => formatEventDateTime(props.item))

  // Convert item to channel format for ChannelIcon component
  const channelData = computed(() => {
    if (props.item.type !== 'channel') return null

    // Create a minimal IChannel object for ChannelIcon
    return {
      _id: props.item.id,
      id: props.item.id,
      name: props.item.label || '',
      slug: props.item.slug || '',
      type: 'text', // Default channel type
      icon: props.item.icon,
      iconType: props.item.iconType,
      // Required IChannel properties with defaults
      unreadCount: 0,
      isAnnouncement: false,
      visibility: 'PUBLIC' as const,
      groupId: '',
      isDefault: false,
      order: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      membersCount: 0,
      postsCount: 0,
      createdBy: '',
    } as unknown as IChannel
  })

  const handleClick = () => {
    props.onClick(props.item)
  }
</script>

<template>
  <div
    :id="`mention-item-${item.id}`"
    role="button"
    tabindex="0"
    :aria-label="`Select ${item.type} mention: ${item.label}`"
    :aria-selected="isSelected"
    :class="['mention-list-item items-center justify-center flex flex-row gap-1', { selected: isSelected }]"
    @mousedown.prevent
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Event Item -->
    <template v-if="item.type === 'event'">
      <!-- Event Image or Icon -->
      <div class="flex-shrink-0">
        <div v-if="item.eventCoverUrl" class="h-10 w-10 overflow-hidden rounded-lg bg-100">
          <OptimisedImage :src="item.eventCoverUrl" :alt="item.label" :width="40" img-class="h-full w-full object-cover" />
        </div>
        <div v-else class="h-10 w-10 overflow-hidden rounded-lg bg-hover flex items-center justify-center">
          <CalendarIcon class="h-6 w-6 text-secondary" />
        </div>
      </div>

      <!-- Event Info -->
      <div class="ml-1 min-w-0 flex-1">
        <CPText size="md" weight="semibold" class="text-700 line-clamp-1 text-left">
          {{ item.label }}
        </CPText>
        <CPText v-if="eventDateTime" size="sm" class="text-secondary text-left">
          {{ eventDateTime.date }}, {{ eventDateTime.timeRange }}
        </CPText>
      </div>
    </template>

    <!-- Channel Item -->
    <template v-else-if="item.type === 'channel' && channelData">
      <!-- Channel Icon -->
      <div class="flex-shrink-0">
        <ChannelIcon :channel="channelData" :size="20" />
      </div>

      <!-- Channel Info -->
      <div class="ml-1 min-w-0 flex-1">
        <CPText size="md" weight="semibold" class="text-700 line-clamp-1 text-left">
          {{ item.label }}
        </CPText>
      </div>
    </template>

    <!-- Course Item -->
    <template v-else-if="item.type === 'course'">
      <div class="flex-shrink-0 h-6 w-6">
        <CPAvatar :src="item.avatar || DEFAULT_IMAGES.COURSES.LOGO" :title="item.label" size="xs" source="courses" />
      </div>

      <div class="ml-1 min-w-0 flex-1">
        <CPText size="md" weight="semibold" class="text-700 line-clamp-1 text-left">
          {{ item.label }}
        </CPText>
      </div>
    </template>

    <!-- Other Types (User, etc.) -->
    <template v-else>
      <!-- Generic Icon -->
      <div class="flex-shrink-0">
        <div class="flex h-6 w-6 items-center justify-center rounded-full bg-200">
          <span class="text-sm text-600">#</span>
        </div>
      </div>

      <!-- Generic Info -->
      <div class="min-w-0 flex-1">
        <CPText size="md" weight="semibold" class="text-700 line-clamp-1 text-left">
          {{ item.label }}
        </CPText>
      </div>
    </template>
  </div>
</template>

<style scoped>
  .mention-list-item {
    width: 100%;
    padding: 12px 8px;
    text-align: left;
    border: none;
    background: var(--cp-brand-background);
    cursor: pointer;
    transition: background-color 0.15s ease;
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--cp-border-600);
  }

  .mention-list-item:hover,
  .mention-list-item:focus,
  .mention-list-item.selected {
    outline: none;
    background-color: var(--cp-font-100);
  }

  .mention-list-item:last-child {
    border-bottom: none;
  }
</style>
